import { buildCatalogModelIdentityIndex, type ModelOffering } from '@bedrock-profiles/shared';

export interface ModelFamily {
  /** Canonical model slug (e.g., "claude-sonnet-4.5"); links go to /models/:key */
  key: string;
  /** Display name (e.g., "Claude Sonnet 4") */
  displayName: string;
  /** Model publisher/owner (e.g., "Anthropic") */
  provider: string;
  /** All offerings in this family */
  offerings: ModelOffering[];
  /** Unique inference providers this family is available on */
  cloudProviders: string[];
  /** Total number of regions */
  regionCount: number;
}

export interface ProviderGroup {
  provider: string;
  count: number;
  families: ModelFamily[];
}

/**
 * Normalize a model ID into a family key by stripping vendor prefixes and version suffixes.
 *
 * e.g., "anthropic.claude-sonnet-4-5-20250929-v1" → "claude-sonnet-4-5"
 *       "us.amazon.nova-micro-v1"                 → "nova-micro"
 *       "gpt-4o-2024-08-06"                       → "gpt-4o"
 *       "gemini-2.5-pro"                          → "gemini-2.5-pro"  (dot is part of version, NOT a vendor prefix)
 *       "gpt-4.1"                                 → "gpt-4.1"         (same)
 *
 * A vendor/routing prefix is purely alphabetic (no digits), e.g. "amazon.", "anthropic.", "us.", "eu.".
 * Dotted version segments like "gemini-2." or "gpt-4." are NOT treated as prefixes.
 */
export function normalizeModelId(modelId: string): string {
  // Fireworks and similar APIs expose account-scoped model resource names.
  // The account is routing metadata, not part of the canonical model family.
  let id = modelId.replace(/^accounts\/[^/]+\/models\//, '');
  // Strip purely-alpha routing/vendor prefixes (no digits allowed), applied twice for
  // nested prefixes like "us.amazon." → "amazon." → "".
  // Regex: starts with one or more lowercase-alpha segments separated by hyphens, then a dot,
  // followed by another lowercase-alpha character (so we don't strip if what's left starts
  // with a digit like "5-pro" from "gemini-2.5-pro").
  for (let i = 0; i < 2; i++) {
    id = id.replace(/^[a-z]+(?:-[a-z]+)*\.(?=[a-z])/, '');
  }

  // Strip version suffixes like -v1, -v2, -20240307, -20250929-v1
  // Also strip date patterns like -2024-08-06
  id = id.replace(/-v\d+$/, '');
  id = id.replace(/-\d{8,}(-v\d+)?$/, '');
  id = id.replace(/-\d{4}-\d{2}-\d{2}$/, '');

  return id;
}

/**
 * Convert a normalized key to a display name
 * e.g., "claude-sonnet-4-5" → "Claude Sonnet 4.5"
 */
export function keyToDisplayName(key: string): string {
  return key
    .split('-')
    .map((part) => {
      if (/^\d+$/.test(part)) return part;
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' ')
    .replace(/(\d+) (\d+)/g, '$1.$2'); // "4 5" → "4.5"
}

/**
 * Group offerings into model families
 */
export function groupIntoFamilies(offerings: ModelOffering[]): ModelFamily[] {
  const activeOfferings = offerings.filter((o) => o.status !== 'REMOVED');
  // Same cross-provider identity as the model pages: the family key is the
  // canonical model slug, so `/models/${family.key}` is always the model page.
  const identity = buildCatalogModelIdentityIndex(activeOfferings);
  const familyMap = new Map<string, ModelFamily>();

  for (const offering of activeOfferings) {
    const provider = offering.model.provider;
    const group = identity.forModel(offering.model.modelId, provider);
    if (!group) continue;
    const mapKey = group.key;

    const existing = familyMap.get(mapKey);
    if (existing) {
      existing.offerings.push(offering);
      if (!existing.cloudProviders.includes(offering.deployment.cloudProvider)) {
        existing.cloudProviders.push(offering.deployment.cloudProvider);
      }
    } else {
      familyMap.set(mapKey, {
        key: group.slug,
        displayName: group.displayName,
        provider,
        offerings: [offering],
        cloudProviders: [offering.deployment.cloudProvider],
        regionCount: 0,
      });
    }
  }

  // Calculate region counts
  for (const family of familyMap.values()) {
    const regions = new Set<string>();
    for (const offering of family.offerings) {
      const destinations = offering.deployment.destinationRegions;
      if (destinations) {
        destinations.forEach((r) => regions.add(r));
      }
      if (offering.deployment.sourceRegion) {
        regions.add(offering.deployment.sourceRegion);
      }
    }
    family.regionCount = regions.size;
  }

  return Array.from(familyMap.values()).sort((a, b) => b.offerings.length - a.offerings.length);
}

/**
 * Group families by provider
 */
export function groupByProvider(families: ModelFamily[]): ProviderGroup[] {
  const providerMap = new Map<string, ProviderGroup>();

  for (const family of families) {
    const existing = providerMap.get(family.provider);
    if (existing) {
      existing.families.push(family);
      existing.count += family.offerings.length;
    } else {
      providerMap.set(family.provider, {
        provider: family.provider,
        count: family.offerings.length,
        families: [family],
      });
    }
  }

  return Array.from(providerMap.values()).sort((a, b) => b.count - a.count);
}
